import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_DELAY_MS = 500;
const MOVE_TOLERANCE_PX = 10;

/**
 * Long-press on a react-map-gl map. Cancels on finger move, multi-touch and touch end.
 * Spread the returned handlers onto <Map>.
 */
export const useMapLongPress = (onLongPress, delay = DEFAULT_DELAY_MS) => {
  const timerRef = useRef(null);
  const startPointRef = useRef(null);

  const cancel = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const onTouchStart = useCallback(
    event => {
      cancel();

      if (event.points?.length !== 1) return;

      startPointRef.current = event.point;
      const { lngLat } = event;

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        onLongPress(lngLat, event.originalEvent);
      }, delay);
    },
    [cancel, delay, onLongPress]
  );

  const onTouchMove = useCallback(
    event => {
      if (!timerRef.current) return;

      if (event.points?.length !== 1) {
        cancel();
        return;
      }

      const start = startPointRef.current;
      const moved = Math.hypot(
        event.point.x - start.x,
        event.point.y - start.y
      );

      if (moved > MOVE_TOLERANCE_PX) cancel();
    },
    [cancel]
  );

  useEffect(() => cancel, [cancel]);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd: cancel,
    onTouchCancel: cancel,
  };
};
